import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatEUR, formatDate, daysUntil, API, auth } from "../lib/api";
import Layout, { Card, Label, Badge, Spinner } from "../components/Layout";
import { HelpInline } from "../components/HelpAndSearch";
import {
  ArrowLeft, Sparkle, ChatCircleText, ClipboardText, Scales, FileText, UsersThree, Calculator, Question, Star, Archive,
  FilePdf, FileDoc, Copy, Tag, BookmarkSimple, CheckCircle, Circle, Handshake, Globe
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview", icon: ClipboardText },
  { key: "analysis", label: "Analisi AI", icon: Sparkle },
  { key: "decision", label: "Bid / No-Bid", icon: Scales },
  { key: "report", label: "Relazione Tecnica", icon: FileText },
  { key: "ati", label: "ATI / Partner", icon: UsersThree },
  { key: "economic", label: "Offerta Economica", icon: Calculator },
  { key: "clarifications", label: "Chiarimenti", icon: Question },
  { key: "presubmit", label: "Pre-Submit", icon: CheckCircle },
];

export default function TenderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenders/${id}`);
      setT(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (loading || !t) return <Layout title="Gara"><Spinner /></Layout>;

  const updateStatus = async (status) => {
    await api.patch(`/tenders/${id}/status`, null, { params: { status } });
    load();
  };

  return (
    <Layout
      title={t.title}
      subtitle={
        <span>
          {t.station_name} • {t.portal} • CIG {t.cig || "—"}
          {t.url && <>{" • "}<a href={t.url} target="_blank" rel="noreferrer" className="text-klein hover:underline font-medium" data-testid="tender-url">↗ Apri sul portale</a></>}
        </span>
      }
      actions={
        <>
          <button onClick={() => nav("/scouting")} className="btn-ghost" data-testid="btn-back">
            <ArrowLeft size={14} className="inline mr-1" /> Torna
          </button>
          <button onClick={async () => {
            try { await api.post(`/tenders/${id}/under-review`); load(); }
            catch (e) { alert(e.response?.data?.detail || e.message); }
          }} className="btn-secondary" data-testid="btn-toggle-review">
            <CheckCircle size={14} className="inline mr-1.5" /> {t.under_review && t.under_review_by === auth.getUser()?.name ? "Rilascia" : "Prendi in esame"}
          </button>
          <button onClick={async () => { await api.post(`/tenders/${id}/auto-tag`); load(); }} className="btn-secondary" data-testid="btn-auto-tag">
            <Tag size={14} className="inline mr-1.5" /> Auto-Tag AI
          </button>
          <button onClick={async () => { const { data } = await api.post(`/tenders/${id}/duplicate`); nav(`/tenders/${data.id}`); }} className="btn-secondary" data-testid="btn-duplicate">
            <Copy size={14} className="inline mr-1.5" /> Duplica
          </button>
          <button onClick={() => window.open(`${API}/tenders/${id}/brief.pdf`, "_blank")} className="btn-secondary" data-testid="btn-brief-pdf">
            <FilePdf size={14} className="inline mr-1.5" /> Brief PDF
          </button>
          <button onClick={async () => {
            const fd = new FormData();
            fd.append("tender_id", id);
            fd.append("signer_name", auth.getUser()?.name || "Admin");
            try {
              const r = await api.post("/documents/sign-pdf", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
              const url = URL.createObjectURL(r.data);
              window.open(url, "_blank");
            } catch (e) { alert("Errore firma: " + e.message); }
          }} className="btn-secondary" data-testid="btn-sign-pdf">
            <FilePdf size={14} className="inline mr-1.5" /> Firma PAdES
          </button>
          <button onClick={() => updateStatus("INTERESTING")} className="btn-secondary" data-testid="btn-mark-interesting">
            <Star size={14} className="inline mr-1.5" /> Interessante
          </button>
          <button onClick={() => updateStatus("ARCHIVED")} className="btn-secondary" data-testid="btn-archive">
            <Archive size={14} className="inline mr-1.5" /> Archivia
          </button>
          <HelpInline page="tender_detail" />
        </>
      }
    >
      {/* Header KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <MetricBox label="Importo base" value={formatEUR(t.base_amount)} testid="m-amount" />
        <MetricBox label="Scadenza" value={`${daysUntil(t.deadline_at)}gg`} sub={formatDate(t.deadline_at)} accent={daysUntil(t.deadline_at) <= 7} testid="m-deadline" />
        <MetricBox label="Procedura" value={t.procedure_type} sub={t.award_criterion} testid="m-procedure" />
        <MetricBox label="Fattibilita" value={
          t.feasibility_label ? <Badge kind={t.feasibility_label.toLowerCase()}>{t.feasibility_label}</Badge> : "—"
        } sub={t.feasibility_score != null ? `score ${t.feasibility_score}/100` : "esegui analisi AI"} testid="m-feas" />
        <MetricBox label="Incumbent" value={t.incumbent_name || "—"} sub={t.incumbent_score != null ? `prob ${t.incumbent_score}/100` : ""} testid="m-incumbent" />
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 mb-4 flex gap-1 overflow-x-auto" data-testid="tabs">
        {TABS.map((T) => {
          const Icon = T.icon;
          const active = tab === T.key;
          return (
            <button key={T.key} data-testid={`tab-${T.key}`} onClick={() => setTab(T.key)}
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors -mb-px whitespace-nowrap ${
                active ? "border-klein text-klein" : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`}>
              <Icon size={15} weight={active ? "fill" : "regular"} />
              {T.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab tender={t} />}
      {tab === "analysis" && <AnalysisTab tender={t} onUpdate={load} />}
      {tab === "decision" && <DecisionTab tender={t} />}
      {tab === "report" && <ReportTab tender={t} />}
      {tab === "ati" && <ATITab tender={t} />}
      {tab === "economic" && <EconomicTab tender={t} />}
      {tab === "clarifications" && <ClarificationsTab tender={t} />}
      {tab === "presubmit" && <PreSubmitTab tender={t} />}
    </Layout>
  );
}

function MetricBox({ label, value, sub, accent, testid }) {
  return (
    <div className={`card-flat p-4 ${accent ? "border-red-300" : ""}`} data-testid={testid}>
      <div className="label-up mb-1">{label}</div>
      <div className="font-display text-xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ============ OVERVIEW ============ */
function OverviewTab({ tender }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <Label>Descrizione</Label>
        <p className="text-sm leading-relaxed text-zinc-700">{tender.description || "Nessuna descrizione."}</p>

        <Label>Testo Disciplinare</Label>
        <div className="text-xs font-mono text-zinc-700 max-h-80 overflow-y-auto whitespace-pre-wrap bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
          {tender.raw_text || "Nessun testo caricato."}
        </div>
      </Card>

      <Card>
        <Label>Dati gara</Label>
        <dl className="text-sm space-y-2">
          <Row k="CIG" v={tender.cig || "—"} mono />
          <Row k="CUP" v={tender.cup || "—"} mono />
          <Row k="Procedura" v={tender.procedure_type} />
          <Row k="Criterio" v={tender.award_criterion} />
          <Row k="Lotti" v={tender.lots_count} />
          <Row k="Pubblicazione" v={formatDate(tender.publication_date)} />
          <Row k="Scadenza" v={formatDate(tender.deadline_at)} />
          <Row k="CPV" v={tender.cpv_codes.join(", ") || "—"} mono />
          <Row k="Regioni" v={tender.regions.join(", ") || "—"} />
          <Row k="Stato" v={<Badge>{tender.status}</Badge>} />
          <Row k="Fonte" v={<Badge kind={tender.is_real ? "aperta" : "default"}>{tender.source || "MANUAL"}</Badge>} />
          {tender.scraped_at && <Row k="Scaricata il" v={new Date(tender.scraped_at).toLocaleString("it-IT")} mono />}
          {tender.original_language && <Row k="Lingua originale" v={tender.original_language} mono />}
          {tender.under_review_by && (
            <Row k="In esame da" v={<span className="text-amber-700 font-semibold">{tender.under_review_by}</span>} />
          )}
          {tender.last_viewed_by && (
            <Row k="Ultima visita" v={<span className="text-xs">{tender.last_viewed_by} • {new Date(tender.last_viewed_at).toLocaleString("it-IT")}</span>} />
          )}
        </dl>

        {tender.viewed_by && tender.viewed_by.length > 0 && (
          <div className="mt-4 pt-3 border-t border-zinc-200">
            <div className="label-up mb-2">Visualizzazioni recenti ({tender.viewed_by.length})</div>
            <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="viewers-list">
              {tender.viewed_by.slice(0, 8).map((v, i) => (
                <li key={i} className="text-xs flex justify-between">
                  <span className="font-medium">{v.user_name}</span>
                  <span className="text-zinc-500 font-mono">{new Date(v.at).toLocaleString("it-IT")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-zinc-100 last:border-0">
      <dt className="text-zinc-500 text-xs uppercase tracking-wider font-mono">{k}</dt>
      <dd className={`text-zinc-900 text-right ${mono ? "font-mono text-xs" : ""}`}>{v}</dd>
    </div>
  );
}

/* ============ ANALYSIS ============ */
function AnalysisTab({ tender, onUpdate }) {
  const [running, setRunning] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const analyze = async () => {
    setRunning(true);
    try {
      await api.post(`/tenders/${tender.id}/analyze`);
      // poll until analysis_status === DONE (max ~120s)
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/tenders/${tender.id}/analysis-status`);
        if (data.analysis_status === "DONE") break;
        if (data.analysis_status === "ERROR") throw new Error("Analisi fallita");
      }
      await onUpdate();
    } catch (e) { alert("Errore analisi: " + (e.response?.data?.detail || e.message)); }
    finally { setRunning(false); }
  };

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setAsking(true); setAnswer("");
    try {
      const { data } = await api.post(`/tenders/${tender.id}/ask`, { question });
      setAnswer(data.answer);
    } finally { setAsking(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="ai-panel">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="label-up text-violet-700">Pipeline AI</div>
            <h3 className="font-display text-lg font-semibold tracking-tight">Analisi documentale con Claude Sonnet 4.5</h3>
            <p className="text-sm text-zinc-600 mt-1">
              Genera executive summary, estrazione requisiti strutturata, griglia di valutazione,
              identificazione incumbent e giudizio di fattibilita.
            </p>
          </div>
          <button onClick={analyze} disabled={running} className="btn-primary whitespace-nowrap" data-testid="btn-analyze">
            {running ? <><span className="spinner mr-2" /> Analizzo...</> : <><Sparkle size={14} className="inline mr-1.5" weight="fill" /> Analizza con AI</>}
          </button>
        </div>
      </Card>

      {tender.ai_summary && (
        <Card data-testid="ai-summary">
          <Label>Executive Summary</Label>
          <div className="prose-tender" dangerouslySetInnerHTML={{ __html: renderMarkdown(tender.ai_summary) }} />
        </Card>
      )}

      {tender.ai_requirements?.length > 0 && (
        <Card data-testid="ai-requirements">
          <Label>Requisiti estratti</Label>
          <table className="w-full text-sm mt-2">
            <thead className="border-b border-zinc-200">
              <tr><Th>Categoria</Th><Th>Descrizione</Th><Th>Escludente</Th><Th>Criticita</Th></tr>
            </thead>
            <tbody>
              {tender.ai_requirements.map((r, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="py-2 px-2 font-mono text-xs">{r.categoria}</td>
                  <td className="py-2 px-2 text-sm">{r.descrizione}</td>
                  <td className="py-2 px-2 text-center">{r.escludente ? "⚠️ SI" : "No"}</td>
                  <td className="py-2 px-2"><Badge kind={r.criticita === "ALTA" ? "compromessa" : r.criticita === "MEDIA" ? "contendibile" : "aperta"}>{r.criticita}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tender.ai_evaluation_grid?.length > 0 && (
        <Card data-testid="ai-grid">
          <Label>Griglia di valutazione tecnica</Label>
          <table className="w-full text-sm mt-2">
            <thead className="border-b border-zinc-200">
              <tr><Th>Criterio</Th><Th className="text-right">Punti</Th><Th>Metodo</Th></tr>
            </thead>
            <tbody>
              {tender.ai_evaluation_grid.map((g, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="py-2 px-2">{g.criterio}</td>
                  <td className="py-2 px-2 text-right font-mono font-semibold">{g.punteggio_max}</td>
                  <td className="py-2 px-2 text-xs text-zinc-600">{g.metodo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card>
        <Label>Chiedi al disciplinare (RAG Q&A)</Label>
        <form onSubmit={ask} className="flex gap-2">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Es: quali sono i requisiti di fatturato?" className="input-field flex-1" data-testid="qa-input" />
          <button disabled={asking} className="btn-primary" data-testid="qa-submit">
            {asking ? "..." : <><ChatCircleText size={14} className="inline mr-1" /> Chiedi</>}
          </button>
        </form>
        {answer && (
          <div className="ai-panel mt-4" data-testid="qa-answer">
            <div className="prose-tender" dangerouslySetInnerHTML={{ __html: renderMarkdown(answer) }} />
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-2 py-2 label-up text-zinc-600 ${className}`}>{children}</th>;
}

/* ============ BID DECISION ============ */
const DIMENSIONS = [
  { k: "strategic_fit", label: "Strategic Fit" },
  { k: "requirement_coverage", label: "Copertura Requisiti" },
  { k: "profit_margin", label: "Margine Atteso" },
  { k: "technical_expertise", label: "Competenza Tecnica" },
  { k: "delivery_capacity", label: "Capacita Esecutiva" },
  { k: "competitive_risk", label: "Rischio Competitivo" },
  { k: "intangible_value", label: "Valore Intangibile" },
  { k: "calendar", label: "Compatibilita Calendario" },
];

function DecisionTab({ tender }) {
  const [decision, setDecision] = useState(null);
  const [scores, setScores] = useState({});
  const [summary, setSummary] = useState("");
  const [risk, setRisk] = useState("");
  const [opp, setOpp] = useState("");
  const [voteVal, setVoteVal] = useState("BID");
  const [voteMot, setVoteMot] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/tenders/${tender.id}/bid-decision`);
    setDecision(data);
    if (data) {
      setScores(data.scores || {});
      setSummary(data.executive_summary);
      setRisk(data.risk_assessment);
      setOpp(data.opportunity_assessment);
    } else {
      setScores(Object.fromEntries(DIMENSIONS.map(d => [d.k, 3])));
    }
  };
  useEffect(() => { load(); }, [tender.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/bid-decisions", {
        tender_id: tender.id, scores, executive_summary: summary, risk_assessment: risk, opportunity_assessment: opp,
      });
      await load();
    } finally { setSaving(false); }
  };

  const vote = async () => {
    if (!decision) return;
    await api.post(`/bid-decisions/${decision.id}/vote`, { vote: voteVal, motivation: voteMot });
    setVoteMot("");
    await load();
  };

  const total = Object.values(scores).reduce((a, b) => a + Number(b || 0), 0);
  const maxTot = DIMENSIONS.length * 5;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <Label>Form di valutazione</Label>
        <div className="space-y-3">
          {DIMENSIONS.map((d) => (
            <div key={d.k} className="flex items-center gap-3" data-testid={`dim-${d.k}`}>
              <div className="flex-1 text-sm font-medium">{d.label}</div>
              <input type="range" min="1" max="5" value={scores[d.k] || 3} onChange={(e) => setScores({ ...scores, [d.k]: parseInt(e.target.value) })} className="w-40" />
              <div className="font-mono w-8 text-center text-sm font-semibold">{scores[d.k] || 3}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-zinc-200 mt-4 pt-4 flex justify-between items-center">
          <div className="text-sm">Score complessivo</div>
          <div className="font-display text-2xl font-bold tracking-tighter">{total} / {maxTot}</div>
        </div>

        <div className="grid grid-cols-1 mt-4 gap-3">
          <Field label="Executive summary"><textarea data-testid="dec-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="input-field" /></Field>
          <Field label="Risk assessment"><textarea data-testid="dec-risk" value={risk} onChange={(e) => setRisk(e.target.value)} rows={2} className="input-field" /></Field>
          <Field label="Opportunita"><textarea data-testid="dec-opp" value={opp} onChange={(e) => setOpp(e.target.value)} rows={2} className="input-field" /></Field>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary mt-4" data-testid="dec-save">
          {saving ? "Salvataggio..." : (decision ? "Aggiorna" : "Invia in revisione")}
        </button>
      </Card>

      <div className="space-y-4">
        {decision && (
          <Card data-testid="dec-state">
            <Label>Stato</Label>
            <div className="font-display text-xl font-bold tracking-tight">{decision.state.replace(/_/g, " ")}</div>
            {decision.final_outcome && (
              <Badge kind={decision.final_outcome === "BID" ? "aperta" : "compromessa"}>
                Decisione: {decision.final_outcome}
              </Badge>
            )}
          </Card>
        )}

        {decision && (
          <Card>
            <Label>Voti ({decision.votes.length})</Label>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto" data-testid="votes-list">
              {decision.votes.length === 0 && <div className="text-sm text-zinc-500">Nessun voto ancora</div>}
              {decision.votes.map((v, i) => (
                <div key={i} className="border-l-2 border-zinc-200 pl-3 py-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium">{v.user_name}</span>
                    <Badge kind={v.vote === "BID" ? "aperta" : v.vote === "NO_BID" ? "compromessa" : "default"}>{v.vote}</Badge>
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">{v.motivation}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-zinc-200 pt-3">
              <div className="label-up">Il tuo voto</div>
              <select value={voteVal} onChange={(e) => setVoteVal(e.target.value)} className="input-field" data-testid="vote-select">
                <option>BID</option><option>NO_BID</option><option>ABSTAIN</option>
              </select>
              <textarea value={voteMot} onChange={(e) => setVoteMot(e.target.value)} rows={2} placeholder="Motivazione (obbligatoria)" className="input-field" data-testid="vote-motivation" />
              <button onClick={vote} disabled={!voteMot} className="btn-primary w-full" data-testid="vote-submit">Vota</button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="label-up mb-1">{label}</div>{children}</div>;
}

/* ============ TECHNICAL REPORT ============ */
function ReportTab({ tender }) {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [tone, setTone] = useState("tecnico-preciso");
  const [focus, setFocus] = useState("");

  const load = async () => {
    const { data } = await api.get(`/tenders/${tender.id}/offer-sections`);
    setSections(data);
    if (!activeId && data.length) setActiveId(data[0].id);
  };
  useEffect(() => { load(); }, [tender.id]);

  const active = sections.find(s => s.id === activeId);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/tenders/${tender.id}/offer-sections/generate`, { section_id: activeId, tone, focus });
      await load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setGenerating(false); }
  };

  const saveContent = async (content) => {
    await api.patch(`/offer-sections/${activeId}`, { content_md: content });
    setSections(sections.map(s => s.id === activeId ? { ...s, content_md: content } : s));
  };

  const setStatus = async (status) => {
    await api.patch(`/offer-sections/${activeId}`, { status });
    setSections(sections.map(s => s.id === activeId ? { ...s, status } : s));
  };

  return (
    <div className="grid grid-cols-12 gap-4 min-h-[600px]">
      <Card className="col-span-12 lg:col-span-3 p-0">
        <div className="p-4 border-b border-zinc-200">
          <div className="flex justify-between items-start">
            <Label>Sezioni Relazione</Label>
            <div className="flex gap-1">
              <button onClick={() => window.open(`${API}/tenders/${tender.id}/relazione-tecnica.pdf`, "_blank")} title="Esporta PDF" className="text-zinc-500 hover:text-klein" data-testid="btn-export-rel-pdf">
                <FilePdf size={16} />
              </button>
              <button onClick={() => window.open(`${API}/tenders/${tender.id}/relazione-tecnica.docx`, "_blank")} title="Esporta DOCX" className="text-zinc-500 hover:text-klein" data-testid="btn-export-rel-docx">
                <FileDoc size={16} />
              </button>
            </div>
          </div>
          <div className="text-xs text-zinc-500 mt-1">{sections.filter(s => s.status === "APPROVED").length}/{sections.length} approvate</div>
        </div>
        <div className="overflow-y-auto max-h-[600px]" data-testid="sections-list">
          {sections.map((s) => (
            <button key={s.id} onClick={() => setActiveId(s.id)}
              className={`w-full text-left px-4 py-2.5 text-sm border-b border-zinc-100 transition-colors ${activeId === s.id ? "bg-klein text-white" : "hover:bg-zinc-50"}`}
              data-testid={`section-item-${s.order}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs opacity-70">{String(s.order + 1).padStart(2, "0")}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                  s.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" :
                  s.status === "AI_DRAFT" ? "bg-violet-100 text-violet-700" :
                  activeId === s.id ? "bg-white/20" : "bg-zinc-100 text-zinc-500"
                }`}>{s.status}</span>
              </div>
              <div className="mt-1 leading-tight">{s.title}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="col-span-12 lg:col-span-9">
        {active && (
          <>
            <div className="flex justify-between items-start mb-3">
              <div>
                <Label>Sezione attiva</Label>
                <h3 className="font-display text-xl font-bold tracking-tight">{active.title}</h3>
              </div>
              <div className="flex gap-2">
                <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-field text-xs max-w-[180px]" data-testid="tone-select">
                  <option value="istituzionale">Istituzionale</option>
                  <option value="tecnico-preciso">Tecnico-preciso</option>
                  <option value="consultivo">Consultivo</option>
                </select>
                <button onClick={generate} disabled={generating} className="btn-primary" data-testid="btn-generate-section">
                  {generating ? <><span className="spinner mr-2" /> Genero...</> : <><Sparkle weight="fill" size={14} className="inline mr-1.5" /> Genera AI</>}
                </button>
              </div>
            </div>

            <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus opzionale (es: 'enfatizza compliance NIS2 e PNRR')" className="input-field text-xs mb-3" data-testid="focus-input" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <Label>Markdown</Label>
                <textarea
                  data-testid="section-editor"
                  value={active.content_md} onChange={(e) => saveContent(e.target.value)}
                  className="input-field font-mono text-xs" rows={20}
                />
              </div>
              <div>
                <Label>Anteprima</Label>
                <div className="prose-tender bg-zinc-50 border border-zinc-200 p-4 rounded-sm h-full max-h-[500px] overflow-y-auto" data-testid="section-preview"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(active.content_md || "*(vuota)*") }} />
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button onClick={() => setStatus("IN_REVIEW")} className="btn-secondary" data-testid="set-review">In revisione</button>
              <button onClick={() => setStatus("APPROVED")} className="btn-primary" data-testid="set-approved">Approva</button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ============ ATI ============ */
function ATITab({ tender }) {
  const [partners, setPartners] = useState([]);
  const [ati, setAti] = useState(null);
  const [type, setType] = useState("ORIZZONTALE");
  const [members, setMembers] = useState([]);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [{ data: ps }, { data: a }] = await Promise.all([
      api.get("/partners"),
      api.get(`/tenders/${tender.id}/ati`),
    ]);
    setPartners(ps);
    setAti(a);
    if (a) {
      setType(a.type);
      setMembers(a.members);
      setNotes(a.notes);
    }
  };
  useEffect(() => { load(); }, [tender.id]);

  const addPartner = (p) => {
    if (members.find(m => m.partner_id === p.id)) return;
    setMembers([...members, { partner_id: p.id, partner_name: p.name, role: members.length === 0 ? "MANDATARIA" : "MANDANTE", quota_participation: 0, quota_execution: 0 }]);
  };

  const updMember = (i, k, v) => {
    const m2 = [...members];
    m2[i] = { ...m2[i], [k]: k.includes("quota") ? parseFloat(v) : v };
    setMembers(m2);
  };

  const removeMember = (i) => setMembers(members.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      await api.post("/ati", { tender_id: tender.id, type, members, notes });
      await load();
      alert("ATI salvata");
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const totalPart = members.reduce((s, m) => s + (m.quota_participation || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card>
        <Label>Partner disponibili</Label>
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="partner-pool">
          {partners.map((p) => (
            <div key={p.id} className="border border-zinc-200 p-3 hover:border-klein cursor-pointer rounded-sm" onClick={() => addPartner(p)} data-testid={`pool-${p.id}`}>
              <div className="text-sm font-medium">{p.name}</div>
              <div className="text-xs text-zinc-500 font-mono mt-0.5">{p.vat}</div>
              <div className="text-xs text-zinc-600 mt-1">{p.sectors.join(", ")}</div>
              {p.certifications.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-1.5">
                  {p.certifications.map((c, i) => <span key={i} className="text-[10px] font-mono bg-zinc-100 px-1.5 py-0.5 rounded-sm">{c}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="lg:col-span-2">
        <div className="flex justify-between mb-3">
          <div>
            <Label>Composizione ATI/RTI</Label>
            <h3 className="font-display text-lg font-bold tracking-tight">{ati ? "Modifica raggruppamento" : "Nuovo raggruppamento"}</h3>
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="input-field max-w-[180px]" data-testid="ati-type">
            <option>ORIZZONTALE</option><option>VERTICALE</option><option>MISTA</option>
          </select>
        </div>

        {members.length === 0 ? (
          <div className="text-sm text-zinc-500 py-12 text-center border border-dashed border-zinc-300 rounded-sm">
            Clicca un partner a sinistra per aggiungerlo al raggruppamento.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="ati-members">
            <thead className="border-b border-zinc-200">
              <tr>
                <Th>Operatore</Th>
                <Th>Ruolo</Th>
                <Th className="text-right">Quota part. %</Th>
                <Th className="text-right">Quota esec. %</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="px-2 py-2">{m.partner_name}</td>
                  <td className="px-2 py-2">
                    <select value={m.role} onChange={(e) => updMember(i, "role", e.target.value)} className="input-field text-xs">
                      <option>MANDATARIA</option><option>MANDANTE</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <input type="number" value={m.quota_participation} onChange={(e) => updMember(i, "quota_participation", e.target.value)} className="input-field text-right w-20" />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <input type="number" value={m.quota_execution} onChange={(e) => updMember(i, "quota_execution", e.target.value)} className="input-field text-right w-20" />
                  </td>
                  <td className="px-2 py-2"><button onClick={() => removeMember(i)} className="text-red-600 hover:underline text-xs">Rimuovi</button></td>
                </tr>
              ))}
              <tr className="font-mono text-xs bg-zinc-50">
                <td colSpan={2} className="px-2 py-2 text-right font-semibold">TOTALE QUOTE %</td>
                <td className={`px-2 py-2 text-right font-bold ${Math.abs(totalPart - 100) > 0.5 ? "text-red-600" : "text-emerald-600"}`}>{totalPart.toFixed(1)}%</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        )}

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Note sul raggruppamento" className="input-field mt-3" data-testid="ati-notes" />

        <button onClick={save} disabled={members.length === 0 || Math.abs(totalPart - 100) > 0.5} className="btn-primary mt-3" data-testid="ati-save">
          Salva ATI
        </button>
      </Card>
    </div>
  );
}

/* ============ ECONOMIC ============ */
function EconomicTab({ tender }) {
  const [eo, setEo] = useState(null);
  const [proposed, setProposed] = useState(tender.base_amount * 0.85);
  const [cp, setCp] = useState(tender.base_amount * 0.5);
  const [cd, setCd] = useState(tender.base_amount * 0.1);
  const [ci, setCi] = useState(tender.base_amount * 0.08);
  const [cc, setCc] = useState(tender.base_amount * 0.03);
  const [competitors, setCompetitors] = useState(5);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/tenders/${tender.id}/economic-offer`);
    if (data) {
      setEo(data);
      setProposed(data.proposed_amount);
      setCp(data.cost_personnel); setCd(data.cost_direct); setCi(data.cost_indirect); setCc(data.cost_contingency);
    }
  };
  useEffect(() => { load(); }, [tender.id]);

  const simulate = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/economic-offer", {
        tender_id: tender.id,
        base_amount: tender.base_amount,
        proposed_amount: proposed,
        cost_personnel: cp, cost_direct: cd, cost_indirect: ci, cost_contingency: cc,
        competitor_count: competitors,
      });
      setEo(data);
    } finally { setSaving(false); }
  };

  const totalCost = cp + cd + ci + cc;
  const discount = ((tender.base_amount - proposed) / tender.base_amount * 100);
  const margin = ((proposed - totalCost) / proposed * 100);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <Label>Parametri simulazione Monte Carlo (semplificata)</Label>

        <div className="space-y-3 mt-3">
          <Slider label="Importo proposto" value={proposed} min={tender.base_amount * 0.5} max={tender.base_amount} onChange={setProposed} fmt={formatEUR} testid="slider-proposed" />
          <Slider label="Costi personale" value={cp} min={0} max={tender.base_amount} onChange={setCp} fmt={formatEUR} testid="slider-cp" />
          <Slider label="Costi diretti" value={cd} min={0} max={tender.base_amount * 0.5} onChange={setCd} fmt={formatEUR} testid="slider-cd" />
          <Slider label="Costi indiretti" value={ci} min={0} max={tender.base_amount * 0.3} onChange={setCi} fmt={formatEUR} testid="slider-ci" />
          <Slider label="Contingency" value={cc} min={0} max={tender.base_amount * 0.2} onChange={setCc} fmt={formatEUR} testid="slider-cc" />
          <Slider label="Numero competitor attesi" value={competitors} min={1} max={20} step={1} onChange={setCompetitors} fmt={(n) => `${n}`} testid="slider-comp" />
        </div>

        <button onClick={simulate} disabled={saving} className="btn-primary mt-4 w-full" data-testid="btn-simulate">
          {saving ? "Simulazione in corso..." : "Esegui simulazione"}
        </button>
      </Card>

      <div className="space-y-3">
        <Card className={eo?.anomaly_risk ? "border-red-300" : ""}>
          <Label>Risultato simulazione</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Stat label="Ribasso" value={`${discount.toFixed(2)}%`} accent={discount > 25} />
            <Stat label="Margine" value={`${margin.toFixed(2)}%`} accent={margin < 5} />
            {eo && <>
              <Stat label="Prob. Vittoria" value={`${(eo.win_probability * 100).toFixed(1)}%`} />
              <Stat label="Valore Atteso" value={formatEUR(eo.expected_value)} />
            </>}
          </div>
          {eo?.anomaly_risk && (
            <div className="border-l-2 border-red-500 bg-red-50 px-3 py-2 text-xs text-red-700 mt-3">
              ⚠️ Possibile rischio di anomalia (ribasso &gt; 25%). Valutare giustificazione costi (art. 110 D.Lgs. 36/2023).
            </div>
          )}
        </Card>

        <Card className="ai-panel">
          <div className="label-up text-violet-700">Logica</div>
          <p className="text-xs text-zinc-600 mt-1">
            La probabilita di vittoria e stimata con una funzione logistica calibrata sul ribasso e sul numero di competitor.
            Il valore atteso = P(vittoria) × Margine. Per analisi piu rigorosa usa <strong>Bayesian Monte Carlo</strong> qui sotto.
          </p>
          <button onClick={async () => {
            try {
              const { data } = await api.post("/economic-offer/bayesian", {
                base_amount: tender.base_amount, proposed_amount: proposed,
                total_cost: cp + cd + ci + cc, competitors: competitors,
              });
              alert(`Bayesian P(win) mean: ${(data.win_probability.mean*100).toFixed(1)}%\nCI 95%: ${(data.win_probability.ci_95_low*100).toFixed(1)}% - ${(data.win_probability.ci_95_high*100).toFixed(1)}%\nExpected Value mean: EUR ${data.expected_value.mean.toFixed(0)}\nBased on prior: ${data.prior.historical_wins} wins / ${data.prior.historical_losses} losses`);
            } catch(e) { alert("Errore: " + e.message); }
          }} className="btn-primary text-xs mt-3 w-full" data-testid="btn-bayesian">
            Calcola Bayesian Monte Carlo
          </button>
        </Card>
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, onChange, fmt, step = 1000, testid }) {
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="label-up">{label}</span>
        <span className="font-mono font-semibold">{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full mt-1" data-testid={testid} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border-l-2 ${accent ? "border-red-500" : "border-klein"} pl-3`}>
      <div className="label-up">{label}</div>
      <div className={`font-display text-2xl font-bold tracking-tighter ${accent ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}

/* ============ CLARIFICATIONS ============ */
function ClarificationsTab({ tender }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/tenders/${tender.id}/clarifications`);
    setItems(data);
  };
  useEffect(() => { load(); }, [tender.id]);

  const add = async () => {
    if (!text.trim()) return;
    await api.post("/clarifications", { tender_id: tender.id, text });
    setText("");
    await load();
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/tenders/${tender.id}/generate-clarifications`);
      await load();
    } finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <div className="flex justify-between mb-3">
          <div>
            <Label>Chiarimenti</Label>
            <h3 className="font-display text-lg font-bold tracking-tight">Gestione domande alla Stazione Appaltante</h3>
          </div>
          <button onClick={generate} disabled={generating} className="btn-primary" data-testid="btn-gen-clarif">
            {generating ? "Generazione..." : <><Sparkle size={14} weight="fill" className="inline mr-1.5" /> Genera con AI</>}
          </button>
        </div>

        <div className="space-y-2 mb-4" data-testid="clarif-list">
          {items.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">Nessun chiarimento ancora</div> :
            items.map((c) => (
              <div key={c.id} className="border-l-2 border-klein bg-zinc-50 p-3 rounded-sm" data-testid={`clarif-${c.id}`}>
                <div className="flex justify-between gap-2">
                  <Badge>{c.type}</Badge>
                  <span className="text-xs text-zinc-500">{formatDate(c.created_at)}</span>
                </div>
                <div className="text-sm mt-2">{c.text}</div>
              </div>
            ))
          }
        </div>

        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Formula una nuova domanda alla SA..." className="input-field" data-testid="clarif-input" />
        <button onClick={add} disabled={!text.trim()} className="btn-primary mt-2" data-testid="clarif-add">Aggiungi domanda</button>
      </Card>

      <Card className="ai-panel">
        <div className="label-up text-violet-700">Smart Clarification AI</div>
        <p className="text-xs text-zinc-600 mt-2">
          L'AI identifica ambiguita nel disciplinare e formula domande strategiche per ottenere
          interpretazioni favorevoli o sfidare requisiti potenzialmente escludenti.
        </p>
        <p className="text-xs text-zinc-600 mt-3">
          Prerequisito: aver eseguito <strong>Analisi AI</strong> sulla gara per disporre del summary e requisiti estratti.
        </p>
      </Card>
    </div>
  );
}

/* ============ PRE-SUBMIT (Preflight + AI advisor) ============ */
function PreSubmitTab({ tender }) {
  const [preflight, setPreflight] = useState(null);
  const [commission, setCommission] = useState(null);
  const [negotiation, setNegotiation] = useState(null);
  const [eu, setEu] = useState(null);
  const [loading, setLoading] = useState({});

  useEffect(() => {
    api.get(`/tenders/${tender.id}/preflight`).then(({ data }) => setPreflight(data));
  }, [tender.id]);

  const runCommission = async () => {
    setLoading({ ...loading, commission: true });
    try {
      const { data } = await api.post(`/tenders/${tender.id}/commission-simulate`);
      setCommission(data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setLoading({ ...loading, commission: false }); }
  };

  const runNegotiation = async () => {
    setLoading({ ...loading, neg: true });
    try {
      const { data } = await api.post(`/tenders/${tender.id}/negotiation-tips`);
      setNegotiation(data);
    } catch (e) { alert(e.message); }
    finally { setLoading({ ...loading, neg: false }); }
  };

  const runEu = async () => {
    setLoading({ ...loading, eu: true });
    try {
      const { data } = await api.post(`/eu/partner-finder`, { tender_id: tender.id, skills_needed: ["software", "consulting"], eu_countries: [] });
      setEu(data);
    } catch (e) { alert(e.message); }
    finally { setLoading({ ...loading, eu: false }); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* PREFLIGHT */}
      <Card data-testid="preflight">
        <Label>Preflight Checklist</Label>
        <h3 className="font-display text-lg font-bold tracking-tight">Sei pronto a sottomettere?</h3>
        {!preflight ? <Spinner /> : (
          <>
            <div className="my-3">
              <div className="flex justify-between text-sm mb-1"><span>Completezza</span><span className="font-mono font-semibold">{preflight.progress}%</span></div>
              <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                <div className={`h-full transition-all ${preflight.ready_to_submit ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${preflight.progress}%` }} />
              </div>
              {preflight.ready_to_submit && <Badge kind="aperta">Pronto per la sottomissione</Badge>}
            </div>
            <ul className="space-y-2 text-sm">
              {preflight.checks.map((c) => (
                <li key={c.key} className="flex items-center gap-2" data-testid={`check-${c.key}`}>
                  {c.ok ? <CheckCircle weight="fill" className="text-emerald-600" size={16} /> : <Circle className="text-zinc-300" size={16} />}
                  <span className={c.ok ? "" : "text-zinc-700"}>{c.label}</span>
                  {c.progress && <span className="text-xs font-mono text-zinc-500 ml-auto">{c.progress}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {/* COMMISSION SIMULATOR */}
      <Card>
        <div className="flex justify-between items-start mb-3">
          <div>
            <Label>AI Commission Simulator</Label>
            <h3 className="font-display text-lg font-bold tracking-tight">Punteggio atteso</h3>
          </div>
          <button onClick={runCommission} disabled={loading.commission} className="btn-primary text-xs" data-testid="btn-commission">
            {loading.commission ? "..." : <><Sparkle weight="fill" size={12} className="inline mr-1" /> Simula</>}
          </button>
        </div>
        {!commission ? (
          <p className="text-sm text-zinc-500">Esegui la simulazione: l'AI valutera la tua offerta come la commissione di gara.</p>
        ) : (
          <>
            <div className="font-display text-4xl font-extrabold tracking-tighter mb-2">{commission.total}<span className="text-xl text-zinc-400">/{commission.total_max}</span></div>
            <Badge kind={commission.perc >= 75 ? "aperta" : commission.perc >= 50 ? "contendibile" : "compromessa"}>{commission.perc}% del max</Badge>
            <div className="mt-3 space-y-1 max-h-60 overflow-y-auto" data-testid="commission-scores">
              {commission.scores.map((s, i) => (
                <div key={i} className="text-xs border-l-2 border-klein pl-2 py-1">
                  <div className="flex justify-between"><span>{s.criterio}</span><span className="font-mono">{s.punteggio}/{s.max}</span></div>
                  <div className="text-zinc-500 text-[10px]">{s.motivazione}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* NEGOTIATION TIPS */}
      <Card>
        <div className="flex justify-between items-start mb-3">
          <div>
            <Label>Negotiation Assistant</Label>
            <h3 className="font-display text-lg font-bold tracking-tight">Leve di negoziazione</h3>
          </div>
          <button onClick={runNegotiation} disabled={loading.neg} className="btn-secondary text-xs" data-testid="btn-negotiation">
            {loading.neg ? "..." : <><Handshake weight="duotone" size={12} className="inline mr-1" /> Suggerisci</>}
          </button>
        </div>
        {!negotiation ? (
          <p className="text-sm text-zinc-500">Per la fase post-aggiudicazione: l'AI suggerisce leve, posizioni e fall-back.</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="neg-tips">
            {(negotiation.tips || []).map((t, i) => (
              <div key={i} className="border-l-2 border-violet-500 bg-violet-50/30 px-3 py-2 text-xs">
                <div className="font-semibold">{t.leva}</div>
                <div className="text-zinc-700 mt-1"><strong>Posizione:</strong> {t.posizione_consigliata}</div>
                <div className="text-zinc-600 mt-0.5"><strong>Fallback:</strong> {t.fall_back}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* EU PARTNER FINDER */}
      {tender.portal === "TED" && (
        <Card>
          <div className="flex justify-between items-start mb-3">
            <div>
              <Label>EU Partner Finder</Label>
              <h3 className="font-display text-lg font-bold tracking-tight">Consorzio europeo</h3>
            </div>
            <button onClick={runEu} disabled={loading.eu} className="btn-secondary text-xs" data-testid="btn-eu">
              {loading.eu ? "..." : <><Globe weight="duotone" size={12} className="inline mr-1" /> Trova partner</>}
            </button>
          </div>
          {!eu ? (
            <p className="text-sm text-zinc-500">Per gare TED: l'AI suggerisce partner UE per il tuo consorzio.</p>
          ) : (
            <div className="space-y-2" data-testid="eu-partners">
              {(eu.partners || []).map((p, i) => (
                <div key={i} className="border border-zinc-200 p-2 rounded-sm text-xs">
                  <div className="font-semibold">{p.name} <span className="text-zinc-500 font-mono">[{p.country}]</span></div>
                  <div className="text-zinc-600">{p.sector}</div>
                  <div className="text-zinc-500 mt-1">{p.why}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ============ Markdown lite ============ */
function renderMarkdown(md) {
  if (!md) return "";
  return md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^- (.*$)/gim, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gims, "<ul>$1</ul>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/^(?!<[hupol])(.+)$/gim, "<p>$1</p>")
    .replace(/<p><\/p>/g, "")
    .replace(/<p>(<[hupol])/g, "$1")
    .replace(/(<\/[hupol][^>]*>)<\/p>/g, "$1");
}
